import OpenInNew from '@mui/icons-material/OpenInNew';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { UserRole } from '~/shared/types';
import {
  FOOTER_CONTACT_URL,
  FOOTER_PRIVACY_POLICY_URL,
  FOOTER_TERMS_OF_SERVICE_URL,
} from '~/shared/constants';
import { FOOTER_SUPPORT_URL, RAILS_EXPERTS_URL } from '~/shared/config';

interface Props {
  className?: string;
}

const Footer = ({ className }: Props) => {
  const { user: currentUser } = useCurrentUser();
  const year = new Date().getFullYear();
  const showSearchPortal = currentUser?.role !== UserRole.EXPERT;

  return (
    <StyledFooter className={className}>
      <CopyrightWrap>
        <Typography variant="caption" color="text.secondary" component="span">
          © {year} Synapsis
        </Typography>
      </CopyrightWrap>
      <LinksRow direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <FooterLink
          href={FOOTER_TERMS_OF_SERVICE_URL}
          color="text.secondary"
          variant="caption"
          underline="hover"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </FooterLink>
        <FooterLink
          href={FOOTER_PRIVACY_POLICY_URL}
          color="text.secondary"
          variant="caption"
          underline="hover"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy
        </FooterLink>
        <FooterLink
          href={FOOTER_CONTACT_URL}
          color="text.secondary"
          variant="caption"
          underline="hover"
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact
        </FooterLink>
        <FooterLink
          href={FOOTER_SUPPORT_URL}
          color="text.secondary"
          variant="caption"
          underline="hover"
          target="_blank"
          rel="noopener noreferrer"
        >
          Support
        </FooterLink>
        {showSearchPortal && (
          <FooterLink
            href={RAILS_EXPERTS_URL}
            color="text.secondary"
            variant="caption"
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
          >
            Search portal
            <OpenInNewIcon fontSize="inherit" />
          </FooterLink>
        )}
      </LinksRow>
    </StyledFooter>
  );
};

export default Footer;

const StyledFooter = styled('footer')(({ theme }) => ({
  marginTop: 'auto',
  padding: theme.spacing(2, 2, 2, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(3, 4, 3, 4),
  },
}));

const CopyrightWrap = styled('div')(({ theme }) => ({
  flexShrink: 0,
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.caption.fontSize,
  padding: theme.spacing(0.5, 0),
  minHeight: 32,
  display: 'inline-flex',
  alignItems: 'center',
}));

const OpenInNewIcon = styled(OpenInNew)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  verticalAlign: 'middle',
  marginLeft: theme.spacing(0.25),
}));

const LinksRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
  },
}));
